import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle, XCircle, AlertCircle, Clock, ArrowLeft, Copy, Info } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { webhookApi } from '../lib/api/webhookApi'

interface WebhookDetailsProps {
  partnerId: string
  webhookId: string
  onBack: () => void
}

export const WebhookDetails: React.FC<WebhookDetailsProps> = ({ partnerId, webhookId, onBack }) => {
  const {
    data: webhookDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['webhooks', 'details', partnerId, webhookId],
    queryFn: () => webhookApi.getWebhookDetails(partnerId, webhookId),
    enabled: !!partnerId && !!webhookId,
  })

  const getStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'pending':
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />
    }
  }

  const getProcessingStatusIcon = (processingStatus?: string) => {
    switch (processingStatus?.toLowerCase()) {
      case 'success':
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'pending':
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500" />
      default:
        return <Info className="h-4 w-4 text-gray-500" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const formatJson = (jsonString: string) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2)
    } catch {
      return jsonString
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Button onClick={onBack} variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <CardTitle>Webhook Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading webhook details...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error || !webhookDetails) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Button onClick={onBack} variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <CardTitle>Webhook Details</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-destructive">Failed to load webhook details</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button onClick={onBack} variant="outline" size="sm">
          <ArrowLeft className="h-4 w-4" />
          Back to List
        </Button>
        <div className="flex items-center gap-2">
          {getStatusIcon(webhookDetails.status)}
          <h1 className="text-2xl font-bold">Webhook Details</h1>
        </div>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Webhook ID</label>
              <div className="flex items-center gap-2">
                <p className="text-sm font-mono">{webhookDetails.webhookId}</p>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(webhookDetails.webhookId)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Partner ID</label>
              <p className="text-sm font-mono">{webhookDetails.partnerId}</p>
            </div>

            {webhookDetails.eventType && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Event Type</label>
                <p className="text-sm">{webhookDetails.eventType}</p>
              </div>
            )}

            {webhookDetails.status && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Status</label>
                <div className="flex items-center gap-2">
                  {getStatusIcon(webhookDetails.status)}
                  <Badge
                    variant={
                      webhookDetails.status === 'success'
                        ? 'default'
                        : webhookDetails.status === 'failed'
                        ? 'destructive'
                        : 'secondary'
                    }
                  >
                    {webhookDetails.status}
                  </Badge>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground">Content Type</label>
              <p className="text-sm font-mono">{webhookDetails.contentType}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Received At</label>
              <p className="text-sm">{formatDate(webhookDetails.receivedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Processing Information */}
      <Card>
        <CardHeader>
          <CardTitle>Processing Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {webhookDetails.processingStatus && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Processing Status</label>
                <div className="flex items-center gap-2">
                  {getProcessingStatusIcon(webhookDetails.processingStatus)}
                  <Badge
                    variant={
                      webhookDetails.processingStatus === 'success'
                        ? 'default'
                        : webhookDetails.processingStatus === 'failed'
                        ? 'destructive'
                        : 'secondary'
                    }
                  >
                    {webhookDetails.processingStatus}
                  </Badge>
                </div>
              </div>
            )}

            {webhookDetails.processingTimeMs !== undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Processing Time</label>
                <p className="text-sm">{webhookDetails.processingTimeMs}ms</p>
              </div>
            )}

            {/* Backward compatibility for legacy processingTime field */}
            {webhookDetails.processingTime !== undefined && webhookDetails.processingTimeMs === undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Processing Time (Legacy)</label>
                <p className="text-sm">{webhookDetails.processingTime}ms</p>
              </div>
            )}

            {webhookDetails.retryCount !== undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Retry Count</label>
                <p className="text-sm">{webhookDetails.retryCount}</p>
              </div>
            )}

            {webhookDetails.errorMessage && (
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-muted-foreground">Error Message</label>
                <div className="flex items-start gap-2">
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded border">{webhookDetails.errorMessage}</p>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(webhookDetails.errorMessage || '')}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle>Timestamps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created At</label>
              <p className="text-sm">{formatDate(webhookDetails.createdAt)}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Updated At</label>
              <p className="text-sm">{formatDate(webhookDetails.updatedAt)}</p>
            </div>

            {webhookDetails.processedAt && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Processed At (Legacy)</label>
                <p className="text-sm">{formatDate(webhookDetails.processedAt)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Headers */}
      {(webhookDetails.xCorrelationId ||
        webhookDetails.xWebhookSignature ||
        webhookDetails.xWebhookTimestamp ||
        webhookDetails.xWebhookDeliveryId ||
        webhookDetails.xWebhookSignatureVersion) && (
        <Card>
          <CardHeader>
            <CardTitle>Webhook Headers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {webhookDetails.xCorrelationId && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">X-Correlation-Id</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono">{webhookDetails.xCorrelationId}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(webhookDetails.xCorrelationId || '')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}

            {webhookDetails.xWebhookDeliveryId && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">X-Webhook-Delivery-Id</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono">{webhookDetails.xWebhookDeliveryId}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(webhookDetails.xWebhookDeliveryId || '')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}

            {webhookDetails.xWebhookSignature && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">X-Webhook-Signature</label>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono break-all">{webhookDetails.xWebhookSignature}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(webhookDetails.xWebhookSignature || '')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}

            {webhookDetails.xWebhookSignatureVersion && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">X-Webhook-Signature-Version</label>
                <p className="text-sm font-mono">{webhookDetails.xWebhookSignatureVersion}</p>
              </div>
            )}

            {webhookDetails.xWebhookTimestamp && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">X-Webhook-Timestamp</label>
                <p className="text-sm font-mono">{webhookDetails.xWebhookTimestamp}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payload */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Payload</CardTitle>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(formatJson(webhookDetails.payload))}>
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted p-4 rounded-md overflow-auto text-sm font-mono max-h-96">
            {formatJson(webhookDetails.payload)}
          </pre>
        </CardContent>
      </Card>
    </div>
  )
}
